<template>
    <!-- <Breadcrumbs main="Setting" mains="Item Management" title="Item Management" /> -->
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Item List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Item
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable :headers="headers" :items="items" :loading="loading" :total="total" :params="params"
                    @update:params="updateParams">
                    <template #item.image="{ item }">
                        <img v-if="item.image" :src="item.image" alt="Item Image"
                            style="max-width: 60px; max-height: 60px;" />
                        <span v-else>-</span>
                    </template>
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-warning me-2" @click="editItem(item as Item)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteItem(item as Item)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="itemModal" tabindex="-1" ref="modalRef"
        aria-labelledby="itemModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                <div class="modal-body">
                    <div class="row">
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Code</label>
                            <input v-model="form.code" type="text" class="form-control" required>
                        </div>
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Name</label>
                            <input v-model="form.name" type="text" class="form-control" required>
                        </div>
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Unit</label>
                            <input v-model="form.unit" type="text" class="form-control">
                        </div>
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Price</label>
                            <input v-model.number="form.price" type="number" class="form-control">
                        </div>
                        <div class="mb-2">
                            <label class="form-label col-form-label">Description</label>
                            <textarea v-model="form.description" class="form-control"></textarea>
                        </div>
                        <div class="mb-2">
                            <label class="form-label col-form-label">Image</label>
                            <Upload pathFile="/master" @on-success="(event: string) => handleImageUpload(event)" @on-change="(event: string) => handleImageUpload(event)"></Upload>
                            <div v-if="form.image" class="mt-2">
                                <img :src="form.image" alt="Preview" style="max-width: 200px; max-height: 200px;">
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                    <button class="btn btn-primary" type="submit" @click="saveItem">Save</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent, ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
const Upload = defineAsyncComponent(() => import("@/components/input/Uploads.vue"))
import { Modal } from 'bootstrap'

// {{ Define Item interface }}
interface Item {
    ID: any; // Allow null for new items
    code: string;
    name: string;
    unit: string;
    price: number;
    description: string;
    image: string; // base64 string
}

const headers = [
    { text: '#', value: 'image' },
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Price', value: 'price' },
    { text: 'Unit', value: 'unit' },
    { text: 'Actions', value: 'actions' }
]

// {{ Apply types to refs and reactive objects }}
const items = ref<Item[]>([])
const loading = ref(false)
const total = ref(0)
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const modalTitle = ref('')
const form = reactive<Item>({
    ID: null,
    code: '',
    name: '',
    unit: '',
    price: 0,
    description: '',
    image: ''
})

// Add this data data array
const dataItems: Item[] = []
import * as master from "@/api/services/MasterRequest";

const fetchItems = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const resp = await master.postRequest("/item/list", { ...params });
        dataItems.splice(0, dataItems.length);
        dataItems.push(...resp.data);

        // Filter based on search
        let filteredItems = [...dataItems]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredItems = dataItems.filter(item =>
                item.code.toLowerCase().includes(searchLower) ||
                item.name.toLowerCase().includes(searchLower)
            )
        }

        // Calculate pagination
        const start = (params.page - 1) * params.size
        const end = start + params.size
        items.value = filteredItems.slice(start, end)
        total.value = filteredItems.length

    } catch (error) {
        toast.error('Failed to fetch items')
    }
    loading.value = false
}

// {{ Add type annotation for newParams }}
const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchItems()
}

const modalRef = ref<HTMLElement | null>(null) // Type the ref
let bsModal: any | null = null // Type the bootstrap modal instance as any since Modal namespace can't be used directly

// View modal state
const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedItem = ref<Item | null>(null)

onMounted(() => {
    if (modalRef.value) { // Check if modalRef exists
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) { // Check if bsModal is initialized
        bsModal.hide()
    }
}

const saveItem = async () => {
    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        // await new Promise(resolve => setTimeout(resolve, 500));
        if (form.ID) {
            // Update existing item in data data
            await master.postRequest("/item/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Item updated successfully');
                    closeModal();
                    fetchItems(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to update item');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update item');
            })
            // const index = dataItems.findIndex(r => r.ID === form.ID);
            // if (index !== -1) {
            //     dataItems[index] = { ...form }; // Update with new form data
            // }
        } else {
            // Add new item to data data
            await master.postRequest("/item/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Item created successfully');
                    closeModal();
                    fetchItems(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to create item');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create item');
            })
        }
    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to save item');
    } finally {
        loading.value = false; // End loading state
    }
}

// {{ Define editItem function }}
const editItem = (item: Item) => {
    openModal(item); // Call openModal with the item data
}


// {{ Add type annotation for item parameter }}
const deleteItem = async (item: Item) => {
    if (!confirm('Are you sure you want to delete this item?')) return

    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        await master.postRequest("/item/delete", { ID: item.ID }).then((resp) => {
            if (resp.status === 200) {
                toast.success('Item deleted successfully');
                closeModal();
                fetchItems(); // Re-fetch to update the table view
            } else {
                toast.error('Failed to delete item');
            }
        })

    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to delete item');
    } finally {
        loading.value = false; // End loading state
    }
}

// Update openModal to ensure accounts array exists
const openModal = (item: Item | null = null) => {
    if (item) {
        modalTitle.value = 'Edit Item'
        Object.assign(form, item);
        // Deep copy accounts to avoid mutating dataItems directly
    } else {
        modalTitle.value = 'Create Item'
        form.ID = null
        form.code = ''
        form.name = ''
    }
    if (bsModal) {
        bsModal.show()
    }
}

// Image upload handler
const handleImageUpload = (event: string) => {
    console.log(event);
    form.image = event
    const reader = new FileReader();
    reader.onload = (e) => {
        form.image = event as string;
    };
}
</script>